import { useCallback, useEffect, useState } from "react";
import { NotAuthenticatedError, api, type Session } from "./api";
import { Login } from "./pages/Login";
import { Overview } from "./pages/Overview";
import { Market } from "./pages/Market";
import { RiskCenter } from "./pages/RiskCenter";
import { Promotions } from "./pages/Promotions";
import { Explain } from "./pages/Explain";
import { AgentControlRoom } from "./pages/AgentControlRoom";
import { Commands } from "./pages/Commands";
import { Audit } from "./pages/Audit";
import { Configuration } from "./pages/Configuration";
import { Users } from "./pages/Users";
import { Account } from "./pages/Account";

type PageId =
  | "overview"
  | "market"
  | "risk"
  | "promotions"
  | "explain"
  | "agents"
  | "commands"
  | "audit"
  | "config"
  | "users"
  | "account";

interface PageDef {
  id: PageId;
  label: string;
  /** Permission the server requires. Used only to decide what to show — the
   * API enforces independently, and a hidden nav item is not a permission
   * check. */
  permission: string;
}

const PAGES: PageDef[] = [
  { id: "overview", label: "Overview", permission: "dashboard.view" },
  { id: "market", label: "Market Monitor", permission: "market.read" },
  // Every role can read this one. An operator who cannot see that the system
  // is halted has no way to know trading has stopped.
  { id: "risk", label: "Risk Center", permission: "kill_switch.read" },
  // Every role can read promotions. Seeing what is queued and what reached
  // production is not a privilege — it is how a change stops being silent.
  { id: "promotions", label: "Promotions", permission: "promotion.read" },
  { id: "explain", label: "Ask", permission: "agent.ask" },
  { id: "agents", label: "Agent Control Room", permission: "agent.ask" },
  { id: "commands", label: "Commands", permission: "command.read" },
  { id: "audit", label: "Audit Log", permission: "audit.read" },
  { id: "config", label: "Configuration", permission: "config.read" },
  { id: "users", label: "Users", permission: "user.manage" },
];

// Everyone has an account, so this one is never permission-gated.
const ACCOUNT_PAGE: PageDef = {
  id: "account",
  label: "Account",
  permission: "",
};

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [checking, setChecking] = useState(true);
  const [page, setPage] = useState<PageId>("overview");

  const signOutLocally = useCallback(() => setSession(null), []);

  // On load, ask the server whether the cookie we may already hold is still
  // good. The cookie is HttpOnly, so its presence cannot be checked from
  // JavaScript — only the server can answer this.
  useEffect(() => {
    api
      .me()
      .then(setSession)
      .catch((err) => {
        if (!(err instanceof NotAuthenticatedError)) {
          console.error("session check failed", err);
        }
      })
      .finally(() => setChecking(false));
  }, []);

  async function signOut() {
    try {
      await api.logout();
    } finally {
      // Clear locally even if the request failed; the cookie may already be
      // dead, and leaving the UI signed in would be misleading.
      signOutLocally();
      setPage("overview");
    }
  }

  if (checking) {
    return (
      <div className="login">
        <p className="dim">Loading…</p>
      </div>
    );
  }

  if (!session) {
    return <Login onSignedIn={setSession} />;
  }

  const visible = [
    ...PAGES.filter((p) => session.permissions.includes(p.permission)),
    ACCOUNT_PAGE,
  ];
  const active = visible.some((p) => p.id === page) ? page : "overview";

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="brand">
          Quant AI
          <small>Trading control plane</small>
        </div>
        <nav className="nav">
          {visible.map((p) => (
            <button
              key={p.id}
              onClick={() => setPage(p.id)}
              aria-current={active === p.id ? "page" : undefined}
            >
              {p.label}
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div>{session.user.username}</div>
          <div className="badge" style={{ margin: "4px 0 8px" }}>
            {session.user.role}
          </div>
          <button className="link" onClick={signOut}>
            Sign out
          </button>
        </div>
      </aside>

      <main className="main">
        {active === "overview" && <Overview onSignedOut={signOutLocally} />}
        {active === "market" && <Market onSignedOut={signOutLocally} />}
        {active === "risk" && (
          <RiskCenter
            onSignedOut={signOutLocally}
            // Passed down only to decide what to render. The API enforces
            // independently — a rendered button is not an authorisation.
            canEngage={session.permissions.includes("kill_switch.engage")}
            canRelease={session.permissions.includes("kill_switch.release")}
          />
        )}
        {active === "promotions" && (
          <Promotions
            onSignedOut={signOutLocally}
            canPropose={session.permissions.includes("promotion.propose")}
            canApprove={session.permissions.includes("promotion.approve")}
            canActivate={session.permissions.includes("promotion.activate")}
          />
        )}
        {active === "explain" && <Explain onSignedOut={signOutLocally} />}
        {active === "agents" && (
          <AgentControlRoom
            onSignedOut={signOutLocally}
            canManage={session.permissions.includes("agent.manage")}
          />
        )}
        {active === "commands" && <Commands onSignedOut={signOutLocally} />}
        {active === "audit" && <Audit onSignedOut={signOutLocally} />}
        {active === "config" && <Configuration onSignedOut={signOutLocally} />}
        {active === "users" && (
          <Users onSignedOut={signOutLocally} currentUserId={session.user.user_id} />
        )}
        {active === "account" && <Account session={session} />}
      </main>
    </div>
  );
}
